import React, { useState } from "react";
import {
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChatBubbleOutlineRounded, MoreHorizRounded, SaveRounded } from "@mui/icons-material";
import { useAiChat } from "../../provider/AiChatProvider";
import dayjs from "dayjs";
import PopUpMenu from "../PopUpMenu/PopUpMenu";
import { apiDelete, apiPatch } from "../../services/Api/apiCall";
import { AiChatThread } from "../../types/AIChat/AiChatThread";

type AiChatHistoryThreadProps = {
  thread: AiChatThread;
  refreshThreads: Function;
};

export default function AiChatHistoryThread({ thread, refreshThreads }: AiChatHistoryThreadProps) {
  const { setActiveThreadId, setHistoryOpen } = useAiChat();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [threadTitle, setThreadTitle] = useState(thread.title);

  const handleThreadClick = () => {
    setActiveThreadId(thread.id);
    setHistoryOpen(false);
  };

  const handleDelete = async () => {
    if (!thread.id) {
      return;
    }
    setIsLoading(true);
    await apiDelete("/ai_chat_threads", thread.id);
    setActiveThreadId(null);
    await refreshThreads();
    setIsLoading(false);
  };

  const handleSaveTitle = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    if (!thread.id) {
      return;
    }
    setIsLoading(true);
    await apiPatch("/ai_chat_threads", thread.id, {
      title: threadTitle,
    });
    await refreshThreads();
    setIsEditing(false);
    setIsLoading(false);
  };

  const formatSecondaryText = (thread: AiChatThread) => {
    const date = dayjs(thread.updatedAt).format("DD.MM.YY");
    const creatorName = thread.createdBy?.person?.fullname;

    return creatorName ? date + ", " + creatorName : date;
  };

  return (
    <ListItem
      disablePadding
      secondaryAction={
        <PopUpMenu icon={<MoreHorizRounded />}>
          <MenuItem onClick={() => setIsEditing(true)}>Umbenennen</MenuItem>
          <MenuItem onClick={handleDelete}>Löschen</MenuItem>
        </PopUpMenu>
      }
    >
      <ListItemButton onClick={handleThreadClick}>
        <ListItemIcon>
          <ChatBubbleOutlineRounded />
        </ListItemIcon>
        <ListItemText
          slotProps={{ secondary: { variant: "caption" } }}
          primary={
            isEditing ? (
              <Stack direction={"row"} sx={{ marginX: "1rem" }} spacing={1}>
                <TextField
                  variant={"standard"}
                  value={threadTitle}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setThreadTitle(e.target.value)}
                />
                <IconButton onClick={handleSaveTitle} disabled={isLoading}>
                  <SaveRounded />
                </IconButton>
              </Stack>
            ) : (
              <Typography>{thread.title}</Typography>
            )
          }
          secondary={formatSecondaryText(thread)}
        />
      </ListItemButton>
    </ListItem>
  );
}
